import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { SectionHeader, StatTile, Pill, EmptyState } from "@/components/Brand";
import {
  Lock,
  Unlock,
  Download,
  ArrowLeft,
  ClipboardList,
  Users,
  Building2,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  CartesianGrid,
} from "recharts";

const BLUE = ["#1d4ed8", "#3b82f6", "#60a5fa", "#93c5fd", "#bfdbfe"];
const SENTIMENT_COLOR = { "Pro-change": "#dc2626", Neutral: "#facc15", "Pro-continuity": "#16a34a" };

export default function CandidateWardReport() {
  const { wardId } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = () => api.get(`/candidate/wards/${wardId}/preview`).then((r) => setData(r.data));
  useEffect(() => {
    load();
  }, [wardId]);

  const purchase = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/candidate/purchase/${wardId}`);
      if (r.data.ok) {
        toast.success("Payment successful (mocked). Report unlocked.");
        load();
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    const r = await api.get(`/candidate/export/ward/${wardId}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${data.ward.name}_report.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;

  const a = data.analytics;
  const unlocked = data.unlocked;

  return (
    <div className="relative">
      <Link to="/candidate" className="inline-flex items-center gap-1 text-sm text-slate-600 mb-3 hover:text-slate-900">
        <ArrowLeft size={14} /> Back to wards
      </Link>
      <SectionHeader
        eyebrow={data.ward.district}
        title={`${data.ward.name} — Ward Report`}
        action={
          unlocked ? (
            <button
              onClick={download}
              data-testid="candidate-download-csv"
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              <Download size={14} /> Download CSV
            </button>
          ) : (
            <button
              onClick={purchase}
              disabled={busy}
              data-testid="candidate-purchase-btn"
              className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white px-5 py-2.5 rounded-md text-sm font-semibold"
            >
              <Unlock size={14} /> {busy ? "Processing…" : `Unlock for ₹${data.price_inr}`}
            </button>
          )
        }
      />

      {!unlocked && (
        <div className="mb-6 p-4 rounded-lg bg-blue-50 border border-blue-100 flex items-start gap-3">
          <AlertCircle className="text-blue-700 mt-0.5" size={18} />
          <div className="text-sm text-blue-900">
            <strong>Preview only.</strong> Unlock the full report to access voter demographics, issue ranking, sentiment heatmap, language profile, and downloadable CSV. Payment is mocked for demo — single click to unlock.
          </div>
        </div>
      )}

      <div className="wi-stat-row mb-6">
        <StatTile label="Total Surveys" value={a.total_surveys} icon={ClipboardList} />
        <StatTile label="Voters Mapped" value={a.voter_counts.total} icon={Users} />
        <StatTile label="Booths" value={data.booths.length} icon={Building2} />
        <StatTile label="Swing Indicator" value={`${a.sentiment.swing_pct}%`} />
      </div>

      <div className={unlocked ? "" : "relative"}>
        {/* Blurred wrapper for locked */}
        <div className={unlocked ? "" : "pointer-events-none select-none filter blur-[6px]"}>
          {a.total_surveys === 0 ? (
            <EmptyState title="No survey data yet" />
          ) : (
            <>
              <div className="grid lg:grid-cols-3 gap-6 mb-6">
                <ChartCard title="Voter Demographics" sub="Gender mix">
                  <Donut data={a.demographics.gender} />
                </ChartCard>
                <ChartCard title="Political Sentiment" sub="Mood">
                  <Donut data={a.sentiment.mood} colorMap={SENTIMENT_COLOR} />
                </ChartCard>
                <ChartCard title="Governance Satisfaction" sub="Satisfaction levels">
                  <Donut data={a.governance.satisfaction} />
                </ChartCard>
              </div>
              <div className="wi-card p-6 mb-6">
                <div className="data-label mb-1">Issues</div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Top ward concerns</h3>
                <div className="space-y-2">
                  {a.issues.slice(0, 8).map((it) => (
                    <div key={it.issue} className="grid grid-cols-12 gap-3 items-center">
                      <div className="col-span-4 text-sm font-medium">{it.issue}</div>
                      <div className="col-span-7">
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-700 rounded-full" style={{ width: `${Math.min((it.score / 3) * 100, 100)}%` }} />
                        </div>
                      </div>
                      <div className="col-span-1 text-right font-mono text-xs">{it.score}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid lg:grid-cols-2 gap-6 mb-6">
                <ChartCard title="Language Profile" sub="Spoken at home">
                  <Bars data={a.migrant_language.languages} />
                </ChartCard>
                <ChartCard title="Voting Behavior" sub="Frequency">
                  <Bars data={a.voting_behavior.behavior} />
                </ChartCard>
              </div>
            </>
          )}
        </div>

        {!unlocked && (
          <div className="absolute inset-0 wi-locked-overlay rounded-lg flex items-center justify-center">
            <div className="text-center bg-white border border-slate-200 shadow-xl rounded-lg p-8 max-w-sm">
              <div className="mx-auto w-12 h-12 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center mb-3">
                <Lock size={20} />
              </div>
              <div className="text-lg font-semibold text-slate-900">Report locked</div>
              <p className="text-sm text-slate-600 mt-1">
                Unlock {data.ward.name}'s full intelligence pack for ₹{data.price_inr}.
              </p>
              <button
                onClick={purchase}
                disabled={busy}
                data-testid="candidate-purchase-overlay"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white px-5 py-2.5 rounded-md text-sm font-semibold"
              >
                <Unlock size={14} /> {busy ? "Processing…" : "Purchase report"}
              </button>
              <div className="mt-2 text-[10px] font-mono text-slate-400 tracking-widest uppercase">Mocked Razorpay flow</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ChartCard({ title, sub, children }) {
  return (
    <div className="wi-card p-5">
      <div className="data-label">{sub}</div>
      <h3 className="text-base font-semibold text-slate-900 mt-1 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Bars({ data }) {
  if (!data || data.length === 0) return <div className="text-xs text-slate-400">No data</div>;
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey="name" />
        <YAxis />
        <Tooltip cursor={{ fill: "#f1f5f9" }} />
        <Bar dataKey="value" fill="#1d4ed8" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function Donut({ data, colorMap }) {
  if (!data || data.length === 0) return <div className="text-xs text-slate-400">No data</div>;
  return (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
          {data.map((d, i) => (
            <Cell key={d.name} fill={(colorMap && colorMap[d.name]) || BLUE[i % BLUE.length]} />
          ))}
        </Pie>
        <Tooltip />
        <Legend wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}
